import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { AbstractCommand } from './AbstractCommand';

import type { VideoEditor } from '../VideoEditor';

/**
 * PLAT-11732 — sets an element's duration and ripples everything after it on
 * all tracks by the difference. Used to fit a blank media card to the length
 * of the video placed in it.
 *
 * Undo restores the timeline snapshot taken before the resize, so the card and
 * every shifted element return exactly where they were.
 */
export class ResizeElementRippleAllTracksCommand extends AbstractCommand {
  private executed = false;

  constructor(
    editor: VideoEditor,
    private readonly elementId: string,
    private readonly newDuration: number,
    private readonly _shouldCreateUndoRedoEntry: boolean = true,
  ) {
    super(editor);
  }

  getName(): string {
    return 'ResizeElementRippleAllTracksCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    if (this.executed) return false;

    const element = this.editor.getElementById(this.elementId);
    if (!element) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Element not found',
        elementId: this.elementId,
      });
      return false;
    }

    super.execute(); // makes a snapshot

    const success = this.editor
      .getTrackManager()
      .resizeElementRippleAllTracks(element, this.newDuration);
    if (!success) return false;

    this.recalculateTimeline();
    this.executed = true;
    return true;
  }

  undo(): boolean {
    if (!this.executed) return false;

    const success = super.undo();
    if (success) {
      this.recalculateTimeline();
      this.editor.markDirty('resize_element_ripple_all_tracks_command_undo');
      this.executed = false;
    }
    return success;
  }

  getDescription(): string {
    return `Resize element ${this.elementId} to ${this.newDuration.toFixed(
      2,
    )}s, rippling all tracks`;
  }
}
